import { observer } from 'mobx-react-lite';
import {
    optInFREWelcomeHeader,
    optInFREEnterpriseWelcomeBody,
    optInFREEnterpriseCutoverWelcomeBody,
    optInFREFastSearchHeader,
    optInFREFastSearchBody,
    optInFREEnterpriseFileHubHeader,
    optInFREEnterpriseFileHubBody,
    optInFREEnterpriseCalendarInviteHeader,
    optInFREEnterpriseCalendarInviteBody,
    optInFREGraduatedAllSetHeader,
    optInFREEnterpriseGraduatedAllSetHeader,
    optInFREEnterpriseGraduatedAllSetBody,
    optInFREEnterpriseGraduatedAllSetLearnMore,
    optInFREAllSetButton,
} from './OptInFirstRunExperience.locstring.json';
import loc from 'owa-localize';
import AllSetScene from './AllSetScene';
import EnterpriseCalendarInviteScene from './EnterpriseCalendarInviteScene';
import EnterpriseFileHubScene from './EnterpriseFileHubScene';
import ExpressionPickerScene from './ExpressionPickerScene';
import FastSearchScene from './FastSearchScene';
import Prose from './Prose';
import Scene from './Scene';
import SmartSuggestScene from './SmartSuggestScene';
import WelcomeScene from './WelcomeScene';
import addCumulativeTime from '../actions/addCumulativeTime';
import incrementViews from '../actions/incrementViews';
import setCurrentPage from '../actions/setCurrentPage';
import setPageStartTime from '../actions/setPageStartTime';
import store from '../store/store';
import { PrimaryButton } from 'office-ui-fabric-react/lib/Button';
import { logUsage } from 'owa-analytics';
import { ModalCarousel, CarouselCardContent } from 'owa-carousel';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getPackageBaseUrl } from 'owa-config';
import { Link } from 'office-ui-fabric-react/lib/Link';

import * as React from 'react';
import { resolve as urlResolve } from 'url';

const styles = require('./OptInFirstRunExperience.scss');
const classNames = require('classnames/bind').bind(styles);

export enum OptInFirstRunExperienceVersion {
    EnterprisePreview,
    EnterpriseCutover,
}

export interface OptInFirstRunExperienceProps {
    onClose?: () => void;
    version: OptInFirstRunExperienceVersion;
    hasStaticArtwork: boolean;
}

function createSceneCard(
    name: string,
    artwork: JSX.Element,
    header: string,
    body: string,
    index: number,
    proseContent?: JSX.Element
): CarouselCardContent {
    return {
        content: () => (
            <Scene
                sceneName={name}
                current={store.currentPage === index}
                artwork={artwork}
                prose={
                    <Prose header={header} body={body}>
                        {proseContent}
                    </Prose>
                }
            />
        ),
        ariaLabel: `${header} ${body}`,
    };
}

function resolveImagePath(name: string) {
    return urlResolve(getPackageBaseUrl(), `resources/images/refresh-fre/${name}`);
}

export default observer(function OptInFirstRunExperience(props: OptInFirstRunExperienceProps) {
    const renderGetStarted = (label: string) => {
        return (
            <PrimaryButton
                text={label}
                onClick={closeCompleteCarousel}
                className={classNames('getStarted')}
            />
        );
    };
    const renderEnterpriseCutOverGetStarted = (label: string) => {
        return (
            <>
                <div>
                    <Link
                        className={styles.learnMore}
                        href="https://go.microsoft.com/fwlink/?linkid=2097892"
                        target="_blank">
                        {loc(optInFREEnterpriseGraduatedAllSetLearnMore)}
                    </Link>
                </div>
                <PrimaryButton
                    text={label}
                    onClick={closeCompleteCarousel}
                    className={classNames('getStarted')}
                />
            </>
        );
    };
    const getCards = (version: OptInFirstRunExperienceVersion) => {
        const art = props.hasStaticArtwork ? getStaticArtwork() : getAnimatedArtwork();
        switch (version) {
            default:
            case OptInFirstRunExperienceVersion.EnterprisePreview:
                if (isFeatureEnabled('doc-fileshub')) {
                    return [
                        createSceneCard(
                            'welcome',
                            art.welcome,
                            loc(optInFREWelcomeHeader),
                            loc(optInFREEnterpriseWelcomeBody),
                            0
                        ),
                        createSceneCard(
                            'search',
                            art.fastSearch,
                            loc(optInFREFastSearchHeader),
                            loc(optInFREFastSearchBody),
                            1
                        ),
                        createSceneCard(
                            'filehub',
                            art.filehub,
                            loc(optInFREEnterpriseFileHubHeader),
                            loc(optInFREEnterpriseFileHubBody),
                            2
                        ),
                        createSceneCard(
                            'calinvite',
                            art.calendarInvite,
                            loc(optInFREEnterpriseCalendarInviteHeader),
                            loc(optInFREEnterpriseCalendarInviteBody),
                            3
                        ),
                        createSceneCard(
                            'letsgo-redux',
                            art.allSet,
                            loc(optInFREGraduatedAllSetHeader),
                            '', // intentionally blank
                            4,
                            renderGetStarted(loc(optInFREAllSetButton))
                        ),
                    ];
                } else {
                    return [
                        createSceneCard(
                            'welcome',
                            art.welcome,
                            loc(optInFREWelcomeHeader),
                            loc(optInFREEnterpriseWelcomeBody),
                            0
                        ),
                        createSceneCard(
                            'search',
                            art.fastSearch,
                            loc(optInFREFastSearchHeader),
                            loc(optInFREFastSearchBody),
                            1
                        ),
                        createSceneCard(
                            'calinvite',
                            art.calendarInvite,
                            loc(optInFREEnterpriseCalendarInviteHeader),
                            loc(optInFREEnterpriseCalendarInviteBody),
                            2
                        ),
                        createSceneCard(
                            'letsgo-redux',
                            art.allSet,
                            loc(optInFREGraduatedAllSetHeader),
                            '', // intentionally blank
                            3,
                            renderGetStarted(loc(optInFREAllSetButton))
                        ),
                    ];
                }
            case OptInFirstRunExperienceVersion.EnterpriseCutover:
                if (isFeatureEnabled('doc-fileshub')) {
                    return [
                        createSceneCard(
                            'welcome',
                            art.welcome,
                            loc(optInFREWelcomeHeader),
                            loc(optInFREEnterpriseCutoverWelcomeBody),
                            0
                        ),
                        createSceneCard(
                            'search',
                            art.fastSearch,
                            loc(optInFREFastSearchHeader),
                            loc(optInFREFastSearchBody),
                            1
                        ),
                        createSceneCard(
                            'filehub',
                            art.filehub,
                            loc(optInFREEnterpriseFileHubHeader),
                            loc(optInFREEnterpriseFileHubBody),
                            2
                        ),
                        createSceneCard(
                            'calinvite',
                            art.calendarInvite,
                            loc(optInFREEnterpriseCalendarInviteHeader),
                            loc(optInFREEnterpriseCalendarInviteBody),
                            3
                        ),
                        createSceneCard(
                            'letsgo-redux',
                            art.allSet,
                            loc(optInFREEnterpriseGraduatedAllSetHeader),
                            loc(optInFREEnterpriseGraduatedAllSetBody),
                            4,
                            renderEnterpriseCutOverGetStarted(loc(optInFREAllSetButton))
                        ),
                    ];
                } else {
                    return [
                        createSceneCard(
                            'welcome',
                            art.welcome,
                            loc(optInFREWelcomeHeader),
                            loc(optInFREEnterpriseCutoverWelcomeBody),
                            0
                        ),
                        createSceneCard(
                            'search',
                            art.fastSearch,
                            loc(optInFREFastSearchHeader),
                            loc(optInFREFastSearchBody),
                            1
                        ),
                        createSceneCard(
                            'calinvite',
                            art.calendarInvite,
                            loc(optInFREEnterpriseCalendarInviteHeader),
                            loc(optInFREEnterpriseCalendarInviteBody),
                            2
                        ),
                        createSceneCard(
                            'letsgo-redux',
                            art.allSet,
                            loc(optInFREEnterpriseGraduatedAllSetHeader),
                            loc(optInFREEnterpriseGraduatedAllSetBody),
                            3,
                            renderEnterpriseCutOverGetStarted(loc(optInFREAllSetButton))
                        ),
                    ];
                }
        }
    };
    const handleCarouselClose = (completion: boolean) => {
        // Track the last page
        recordCurrentPage();
        // Log views & cumulative time for each card
        let totalFRETime = 0;
        for (const i of Object.keys(store.views)) {
            totalFRETime += store.cumulativeTime[i];
            logUsage(
                'viewFRECard',
                [
                    mapCardIndexToName(Number(i)),
                    props.hasStaticArtwork,
                    store.views[i],
                    store.cumulativeTime[i],
                ],
                { isCore: true }
            );
        }
        logUsage('closeFRE', [
            completion,
            mapCardIndexToName(store.currentPage),
            props.hasStaticArtwork,
            totalFRETime,
        ]);
        if (props.onClose) {
            props.onClose();
        }
    };
    const closeIncompleteCarousel = () => handleCarouselClose(false);
    const closeCompleteCarousel = () => handleCarouselClose(true);
    return (
        <ModalCarousel
            isOpen={true}
            onClose={closeIncompleteCarousel}
            closeClassName={classNames('closeButton')}
            navigationClassName={classNames('freColor', 'hoverFREColor')}
            paginationClassName={classNames('freColor')}
            onCardChange={handlePageChange}
            cardContent={getCards(props.version)}
        />
    );
});

function getStaticArtwork() {
    return {
        welcome: <img src={resolveImagePath('welcome.png')} />,
        fastSearch: <img src={resolveImagePath('fastsearch.png')} />,
        expressionPicker: <img src={resolveImagePath('expressionpicker.png')} />,
        smartSuggest: <img src={resolveImagePath('smart-suggest.png')} />,
        allSet: <img src={resolveImagePath('youreallset.png')} />,
        filehub: <img src={resolveImagePath('rtl-placeholder-filehub.png')} />,
        calendarInvite: <img src={resolveImagePath('rtl-placeholder-calinvite.png')} />,
    };
}

function getAnimatedArtwork() {
    return {
        welcome: <WelcomeScene />,
        fastSearch: <FastSearchScene />,
        expressionPicker: <ExpressionPickerScene />,
        smartSuggest: <SmartSuggestScene />,
        allSet: <AllSetScene />,
        filehub: <EnterpriseFileHubScene />,
        calendarInvite: <EnterpriseCalendarInviteScene />,
    };
}

function mapCardIndexToName(cardIndex: number) {
    switch (cardIndex) {
        case 0:
            return 'welcome';
        case 1:
            return 'search';
        case 2:
            return 'filehub';
        case 3:
            return 'calinvite';
        case 4:
            return 'expression';
        case 5:
            return 'letsgo';
        default:
            return 'unknown';
    }
}

function recordCurrentPage() {
    // Track cumulative time and view count for the current page
    const currentPage = store.currentPage;
    addCumulativeTime(currentPage, new Date().getTime() - store.pageStartTime);
    incrementViews(currentPage);
}

function handlePageChange(page: number) {
    recordCurrentPage();
    // Set current page to enable animations
    setCurrentPage(page);
    // Start tracking time for the new page
    setPageStartTime(new Date().getTime());
}
